import React, { useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import ApiService from '../../../../services/ApiService'
import { PurchaseDemand } from './types'
import {
  CloseButton,
  ErrorText,
  MiniTable,
  Overlay,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PrimaryButton,
  SecondaryButton,
  TotalRow,
} from '../ModalStyle'

interface IssueOrderModalProps {
  mode: 'QUOTE' | 'ORDER'
  supplierCompanyId: number
  supplierName: string
  demands: PurchaseDemand[]
  onClose: () => void
  onSuccess: () => void
}

interface LineState {
  purchaseDemandId: number
  name: string
  code: string
  unit: string
  stock: number
  needed: number
  quantity: number
  unitCost: number
}

const IssueOrderModal: React.FC<IssueOrderModalProps> = ({
  mode,
  supplierCompanyId,
  supplierName,
  demands,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate()
  const [lines, setLines] = useState<LineState[]>(() =>
    demands.map((demand) => ({
      purchaseDemandId: demand.id,
      name: demand.PurchaseMaterial.name,
      code: demand.PurchaseMaterial.code || '-',
      unit: demand.PurchaseMaterial.UnitOfMeasurement?.name || '',
      stock: Number(demand.PurchaseMaterial.currentStock),
      needed: Number(demand.requiredQuantity),
      quantity: Number(demand.quantityToBuy),
      unitCost: Number(demand.PurchaseMaterial.unitCost ?? 0),
    })),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const total = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity * line.unitCost, 0),
    [lines],
  )

  const updateQuantity = (purchaseDemandId: number, value: string) => {
    const quantity = value === '' ? 0 : Number(value)
    setLines((prev) =>
      prev.map((line) => (line.purchaseDemandId === purchaseDemandId ? { ...line, quantity } : line)),
    )
  }

  const handleConfirm = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      await ApiService.purchaseOrder.createFromDemands({
        mode,
        supplierCompanyId,
        items: lines.map((line) => ({
          purchaseDemandId: line.purchaseDemandId,
          quantity: line.quantity,
          unitCost: line.unitCost,
        })),
      })
      onSuccess()
      onClose()
      if (mode === 'ORDER') {
        navigate('/panel/purchase/orders')
      }
    } catch (err) {
      console.error('Erro ao gerar ordem de compra', err)
      setError('Não foi possível concluir a operação. Verifique as quantidades e tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Panel $width='42rem' onClick={(e) => e.stopPropagation()}>
        <PanelHeader>
          <h3>{mode === 'QUOTE' ? 'Solicitar cotação' : 'Emitir pedido'} — {supplierName}</h3>
          <CloseButton onClick={onClose}>
            <X size={18} />
          </CloseButton>
        </PanelHeader>
        <PanelBody>
          <MiniTable>
            <thead>
              <tr>
                <th>Item</th>
                <th>Em estoque</th>
                <th>Necessário</th>
                <th>Qtd. a comprar</th>
                <th>Custo unit.</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.purchaseDemandId}>
                  <td>
                    {line.name}
                    <br />
                    <span style={{ color: '#969696' }}>{line.code}</span>
                  </td>
                  <td>
                    {line.stock} {line.unit}
                  </td>
                  <td>
                    {line.needed} {line.unit}
                  </td>
                  <td>
                    <input
                      type='number'
                      min={0}
                      value={line.quantity}
                      onChange={(e) => updateQuantity(line.purchaseDemandId, e.target.value)}
                    />
                  </td>
                  <td>R$ {line.unitCost.toFixed(2)}</td>
                  <td>R$ {(line.quantity * line.unitCost).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </MiniTable>
          <TotalRow>
            Total do pedido: <strong>R$ {total.toFixed(2)}</strong>
          </TotalRow>
          {error && <ErrorText>{error}</ErrorText>}
        </PanelBody>
        <PanelFooter>
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton disabled={saving} onClick={handleConfirm}>
            {saving ? 'Salvando...' : mode === 'QUOTE' ? 'Criar cotação' : 'Criar pedido'}
          </PrimaryButton>
        </PanelFooter>
      </Panel>
    </Overlay>
  )
}

export default IssueOrderModal
